import React, { RefObject, useEffect, useRef } from 'react';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Box } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import FilterDropdown from '@rover/react-lib/src/pages/search/SearchPage/components/SimplifiedFiltersBar/components/FilterDropdown';

import RevampDatesPicker, { DateSelection } from './RevampDatesPicker';

const MONTHS_SHOWN = 2;
// A month is seven 40px day cells wide, so two of them plus the gap and the
// panel padding decide how far it has to widen past its trigger. It grows
// towards the page instead of off the right edge of the sidebar.
const MONTH_WIDTH = '296px';
const MONTHS_GAP = '24px';
const DROPDOWN_WIDTH = 'min(680px, 92vw)';

type Props = DateSelection & {
  onClose: () => void;
  onSave: (selection: DateSelection) => void;
  locale: Locale;
  allowsLooseDates?: boolean;
  allowsRecurring?: boolean;
  triggerRef: RefObject<HTMLElement | null>;
};

const RevampDatesDropdown = ({
  onClose,
  onSave,
  locale,
  startDate,
  endDate,
  dates,
  isRecurring,
  weekDays,
  allowsLooseDates,
  allowsRecurring,
  triggerRef,
}: Props): JSX.Element => {
  const theme = useTheme();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // The panel owns the Escape handler, so it has to hold focus while it is open;
  // focus goes back to the row that opened it once it closes.
  useEffect(() => {
    dropdownRef.current?.focus();

    return () => {
      triggerRef.current?.focus();
    };
  }, [triggerRef]);

  return (
    <FilterDropdown
      ref={dropdownRef}
      onClose={onClose}
      triggerRef={triggerRef}
      role="dialog"
      aria-label="Dates"
      align="right"
      width={DROPDOWN_WIDTH}
    >
      <Box
        p="4x"
        borderRadius="8px"
        backgroundColor={theme.background.primary}
        sx={{
          '.DayPicker-Months': {
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'center',
            gap: MONTHS_GAP,
          },
          '.DayPicker-Month': {
            flex: `0 0 ${MONTH_WIDTH}`,
            width: MONTH_WIDTH,
            margin: 0,
          },
        }}
      >
        <RevampDatesPicker
          isOpen
          onClose={onClose}
          onSave={onSave}
          locale={locale}
          startDate={startDate}
          endDate={endDate}
          dates={dates}
          isRecurring={isRecurring}
          weekDays={weekDays}
          allowsLooseDates={allowsLooseDates}
          allowsRecurring={allowsRecurring}
          monthsShown={MONTHS_SHOWN}
          testID="revamp-dates-dropdown"
        />
      </Box>
    </FilterDropdown>
  );
};

export default RevampDatesDropdown;
